import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';

import {
  AuthorModel,
  BlogsListModel,
  ContactUsFormModel,
  CreateUserModel, JointUsFormModel, PostModel,
  RequestDemoFormModel,
  ResponseModel,
  SignUpModel
} from '../models/sign-up.model';
import { ComingSoonPageTypeEnum } from "../../../models/coming-soon-page.model";
import {BranchDataModel, FeedbackPageModel} from "../../external-pages/feedback/feedback/models";
import {Params} from "@angular/router";
import { LocaleService } from 'src/app/locale/locale.service';

@Injectable()
export class ApiService {
  private readonly apiGetStarted: string = '/get-started/send-request';
  private readonly apiFindUser: string = '/get-started/find-user';
  private readonly apiCreateUser: string = '/get-started/create-user';
  private readonly apiContactUs: string = '/contact-us/add';
  private readonly apiJointUs: string = '/contact-us/join-runple-team';
  private readonly apiRequestDemo: string = '/get-started/save-demo';
  private readonly apiFeedback: string = '/feedback';
  private readonly apiEmailUnsubscribe: string = '/email-unsubscribe';

  constructor(
    private readonly http: HttpClient,
    private readonly locale: LocaleService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
  }

  setEntryPointFromUrl(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      const url = new URL(window.location.href);
      const entryPoint = url.searchParams.get('entryPoint');
      if (entryPoint) {
        sessionStorage.setItem('entryPoint', entryPoint);
        console.log(entryPoint);
      }
    } catch {
      // no-op: ignore malformed URLs or restricted access
    }
  }

  public sendRequest(data: SignUpModel): Observable<any> {
    const billingChannel = sessionStorage.getItem('entryPoint');
    const payload = {
      ...data,
      type: 'RUNPLE',
      ...(billingChannel ? { billingChannel } : {}),
    };

    return this.http.post<Observable<any>>(this.apiGetStarted, payload);
  }

  public findUser(token: string): Observable<any> {
    return this.http.get<Observable<any>>(`${this.apiFindUser}/${token}`);
  }

  public createUser(data: CreateUserModel, token: string): Observable<any> {
    return this.http.post<Observable<any>>(`${this.apiCreateUser}/${token}`, {...data});
  }

  public emailResend(token: string): Observable<any> {
    return this.http.get<ResponseModel<any>>( `/get-started/resend-email-verify/${token}`);
  }

  public contactUs(data: ContactUsFormModel): Observable<any> {
    return this.http.post<Observable<any>>(this.apiContactUs, {...data});
  }

  public jointUs(data: JointUsFormModel): Observable<any> {
    return this.http.post<Observable<any>>(this.apiJointUs, {...data});
  }

  public requestDemo(data: RequestDemoFormModel): Observable<any> {
    return this.http.post<Observable<any>>(this.apiRequestDemo, {...data});
  }

  public validateWorkspaceDomain(domain: string): Observable<any> {
    const params = new HttpParams().set('domain', domain);
    return this.http.get<ResponseModel<any>>('/hq/v1/workspace/validate', {params})
      .pipe(
        map((response: ResponseModel<any>) => {
          return response.data;
        })
      );
  }

  public getLegalTypes(countryCodeIso3 = 'AUT'): Observable<any> {
    const params = new HttpParams().set('countryCodeIso3', countryCodeIso3);
    return this.http.get<ResponseModel<any>>('/accounting/v1/utils/profile/legal-types', { params });
  }

  public getArticleList(): Observable<BlogsListModel> {
    return this.http.get<any>('/blog/list');
  }

  public getArticleListByCategory(id: number): Observable<BlogsListModel> {
    return this.http.get<any>(`/blog/list/category/${id}`);
  }

  public getPost(name: string): Observable<PostModel> {
    return this.http.get<any>(`/blog/post/${name}`);
  }

  public getLexiconList(): Observable<any> {
    return this.http.get<any>(`/lexicon/list`);
  }

  public getLexiconListByCategory(id: number): Observable<BlogsListModel> {
    return this.http.get<any>(`/lexicon/list/category/${id}`);
  }

  public getLexicon(name: string): Observable<PostModel> {
    return this.http.get<any>(`/lexicon/post/${name}`);
  }

  public getAuthorProfile(name: string): Observable<AuthorModel> {
    return this.http.get<any>(`/authors/authors-page/${name}`);
  }

  public emailSubscription(email: string, type: ComingSoonPageTypeEnum): Observable<any> {
    const params = {
      email: email,
      type: type,
      isoCode: this.locale.userLocale$.getValue(),
    };

    return this.http.post<ResponseModel<any>>('/get-started/email-subscription', params);
  }

  public tradeOfferConfirmation(token: string): Observable<any> {
    return this.http.post(`/api/v1/trade-offers/reply`,
      {
        secret: token
      }
    );
  }

  public allowedWorkspace(email: string): Observable<any> {
    return this.http.post(`/api/v1/auth/allowed-workspaces`,
      {
        email: email
      }
    );
  }

  public getFeedbackPage(id: string): Observable<FeedbackPageModel> {
    return this.http.get<FeedbackPageModel>(`${this.apiFeedback}/page/${id}`);
  }

  public getFeedbackBranch(id: string, branchId: number): Observable<BranchDataModel> {
    return this.http.get<BranchDataModel>(`${this.apiFeedback}/branch-options/${id}/${branchId}`);
  }

  public saveFeedbackResult(resultId: number, data: any): Observable<any> {
    return this.http.patch<any>(`${this.apiFeedback}/save-result/${resultId}`, {data});
  }

  public changeWorkspaceStatus(uid: string, status: 'DELETE'|'REVOKE'): Observable<FeedbackPageModel> {
    const body = {uid, status};
    return this.http.patch<FeedbackPageModel>(`${this.apiFeedback}/change-workspace-status`, body);
  }

  public checkEmailSubscriptionStatus(email: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiEmailUnsubscribe}/check-email/${email}`);
  }

  public changeEmailSubscriptionStatus(email: string): Observable<{status: 'error'|'success'}> {
    const body = { email };
    return this.http.post<{status: 'error'|'success'}>(`${this.apiEmailUnsubscribe}/unsubscribe`, body);
  }

  public getShopifyConnection(sessionToken: string): Observable<any> {
    const params = { sessionToken };
    return this.http.get('/ecommerce/v1/integration/shopify/connection', { params });
  }

  public addShopifyConnection(sessionToken: string, token: string): Observable<any> {
    const params = { sessionToken, token };
    return this.http.post('/ecommerce/v1/integration/shopify/connection', {}, { params });
  }

  public deleteShopifyConnection(sessionToken: string): Observable<any> {
    const params = { sessionToken };
    return this.http.delete('/ecommerce/v1/integration/shopify/connection', { params });
  }

  public grantShopify(params: Params): Observable<any> {
    return this.http.get('/ecommerce/v1/integration/shopify/grant', { params });
  }

  public oauthShopify(params: Params): Observable<any> {
    return this.http.get('/ecommerce/v1/integration/shopify/oauth', { params });
  }
}
